import classNames from 'classnames/bind';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';

import Button from 'uikit/Button';
import Icon from 'uikit/Icon';
import InputDateTime from 'uikit/InputDateTime';
import InputText from 'uikit/InputText';
import SubPage from 'uikit/SubPage';
import Switch from 'uikit/Switch';
import Tooltip from 'uikit/Tooltip';

import styles from './ChapterEdit.module.scss';

const cx = classNames.bind(styles);

const ChapterEdit = ({
  course,
  handleChooseDateChange,
  handleClose,
  handleIsOfflineChange,
  handleSave,
  handleTitleEditToggle,
  initialValues,
  isEditingTitle,
  isModalOpen,
  t,
  validationSchema,
}) => (
  <SubPage headerSize="default" isOpen={isModalOpen} onExit={handleClose}>
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSave}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, setFieldValue, status, values }) => (
        <Form className={cx('subpage')}>
          <div className={cx('header')}>
            <div className={cx('icon')}>
              <Icon name="folder-open" />
            </div>
            {isEditingTitle ? (
              <div className={cx('title-header')}>
                <InputText
                  autoFocus
                  className={cx('title-edit')}
                  onBlur={handleTitleEditToggle}
                  name="title"
                  maxLength={256}
                />
                <Button icon="check" intent="white" />
              </div>
            ) : (
              <div className={cx('title-header')}>
                <p className={cx('title-view')}>{values.title}</p>
                <Button
                  icon="pencil"
                  intent="white"
                  onClick={handleTitleEditToggle}
                  size="small"
                />
              </div>
            )}
            <div className={cx('fill')} />
            <Button intent="primary" isLoading={isSubmitting} type="submit">
              {t('Course/Teacher/Editor/ChapterEdit/save-chapter')}
            </Button>
          </div>
          {status === '500' && (
            <p className={cx('error')} role="alert">
              {t('Course/Teacher/Editor/ChapterEdit/chapter-edit-error')}
            </p>
          )}
          <div className={cx('tiles')}>
            {!course.isLinear && (
              <div className={cx('tile')}>
                <p className={cx('tile-title')}>
                  {t('Course/Teacher/Editor/ChapterEdit/method-title')}
                </p>
                <Switch
                  className={cx('field')}
                  helper={
                    <Tooltip
                      tooltip={t(
                        'Course/Teacher/Editor/ChapterEdit/student-choice-helper',
                      )}
                    >
                      <Icon name="info-circle" />
                    </Tooltip>
                  }
                  label={t(
                    'Course/Teacher/Editor/ChapterEdit/student-choice-label',
                  )}
                  name="studentChoice"
                  onChange={() =>
                    values.isLinked
                      ? setFieldValue('studentChoice', true) &&
                        setFieldValue('isLinked', false)
                      : setFieldValue('studentChoice', false) &&
                        setFieldValue('isLinked', true)
                  }
                />
                <Switch
                  className={cx('field')}
                  helper={
                    <Tooltip
                      tooltip={t(
                        'Course/Teacher/Editor/ChapterEdit/progressive-chapter-helper',
                      )}
                    >
                      <Icon name="info-circle" />
                    </Tooltip>
                  }
                  label={t('Course/Teacher/Editor/ChapterEdit/linked-label')}
                  name="isLinked"
                  onChange={() =>
                    values.isLinked
                      ? setFieldValue('studentChoice', true) &&
                        setFieldValue('isLinked', false)
                      : setFieldValue('studentChoice', false) &&
                        setFieldValue('isLinked', true)
                  }
                />
              </div>
            )}
            <div className={cx('tile')}>
              <p className={cx('tile-title')}>
                {t('Course/Teacher/Editor/ChapterEdit/online-dates')}
              </p>
              <Switch
                className={cx('field')}
                label={t('Course/Teacher/Editor/ChapterEdit/isOffline-label')}
                name="isOffline"
                onChange={() =>
                  handleIsOfflineChange({ setFieldValue, values })
                }
              />
              <Switch
                className={cx('field')}
                label={t('Course/Teacher/Editor/ChapterEdit/choose-date-label')}
                name="chooseDate"
                onChange={() =>
                  handleChooseDateChange({
                    setFieldValue,
                    values,
                  })
                }
              />
              {values.chooseDate && (
                <>
                  <InputDateTime
                    className={cx('field')}
                    label={t(
                      'Course/Teacher/Editor/ChapterEdit/online-timestamp-label',
                    )}
                    name="onlineTimestamp"
                  />
                  <InputDateTime
                    className={cx('field')}
                    label={t(
                      'Course/Teacher/Editor/ChapterEdit/offline-timestamp-label',
                    )}
                    name="offlineTimestamp"
                  />
                </>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  </SubPage>
);

ChapterEdit.propTypes = {
  course: PropTypes.object.isRequired,
  handleChooseDateChange: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleIsOfflineChange: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleTitleEditToggle: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  isEditingTitle: PropTypes.bool.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  validationSchema: PropTypes.object.isRequired,
};

export default ChapterEdit;
